import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const ease = [0.16, 1, 0.3, 1];

export default function ProjectDetail({ project, onClose }) {
  const { lang, t } = useLang();
  const [idx, setIdx] = useState(0);

  const gallery = project?.gallery || [];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % gallery.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + gallery.length) % gallery.length);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [gallery.length, onClose]);

  if (!project) return null;

  const current = gallery[idx] || { url: project.image, label: { fr: "Image", en: "Image" }, type: "" };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4, ease }}
        className="fixed inset-0 z-[100] bg-[#0A0A0A] text-[#F7F7F9] overflow-y-auto"
        data-testid="project-detail-modal"
      >
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur-md border-b border-[#F7F7F9]/10">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <span className="font-display text-lg md:text-xl tracking-tight">LAKA</span>
              <span className="hidden md:inline font-mono text-[10px] tracking-[0.25em] uppercase text-[#F7F7F9]/50">
                Project — {project.id}
              </span>
            </div>
            <button
              data-testid="project-detail-close"
              onClick={onClose}
              className="flex items-center gap-2 font-mono text-[11px] tracking-[0.22em] uppercase hover:text-[#D35400] transition-colors"
            >
              <span className="hidden md:inline">{lang === "fr" ? "Fermer" : "Close"}</span>
              <span className="w-10 h-10 border border-[#F7F7F9]/30 flex items-center justify-center hover:border-[#D35400] transition-colors">
                <X size={18} />
              </span>
            </button>
          </div>
        </div>

        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-14 md:py-20">
          {/* Header grid */}
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-14 md:mb-20">
            <div className="col-span-12 md:col-span-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease, delay: 0.1 }}
                className="flex items-center gap-3 mb-6"
              >
                <span className="w-8 h-px bg-[#F7F7F9]/80" />
                <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#F7F7F9]/70">
                  {t.projects.overline} — {project.year}
                </span>
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease, delay: 0.15 }}
                className="font-display font-medium tracking-tighter leading-[0.95] text-[clamp(2.5rem,7vw,6rem)] max-w-[18ch]"
              >
                {project.title[lang]}
              </motion.h2>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.25 }}
              className="col-span-12 md:col-span-4 space-y-5 md:pt-10"
            >
              <Meta k={lang === "fr" ? "Lieu" : "Location"} v={project.location} />
              <Meta k={lang === "fr" ? "Année" : "Year"} v={project.year} />
              <Meta k={lang === "fr" ? "Catégorie" : "Category"} v={t.projects.filters[project.category] || project.category} />
              <Meta k={lang === "fr" ? "Périmètre" : "Scope"} v={project.scope} />
              {project.surface && (
                <Meta k={lang === "fr" ? "Surface" : "Surface"} v={project.surface} />
              )}
              {project.budget && (
                <Meta
                  k={lang === "fr" ? "Budget" : "Budget"}
                  v={typeof project.budget === "string" ? project.budget : project.budget[lang]}
                />
              )}
            </motion.div>
          </div>

          {/* Main image / video viewer */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: 0.3 }}
            className="relative bg-[#1A1A1A] border border-[#F7F7F9]/10 aspect-[16/10] md:aspect-[16/9] overflow-hidden mb-6 group"
          >
            <AnimatePresence mode="wait">
              {current.kind === "video" ? (
                <motion.video
                  key={current.url}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, ease }}
                  src={current.url}
                  controls
                  autoPlay
                  loop
                  playsInline
                  className="w-full h-full object-contain bg-[#0A0A0A]"
                  data-testid="project-video"
                />
              ) : (
                <motion.img
                  key={current.url}
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease }}
                  src={current.url}
                  alt={current.label?.[lang] || "Drawing"}
                  className="w-full h-full object-contain bg-[#FAFAF7]"
                />
              )}
            </AnimatePresence>

            {/* Technical overlays */}
            <div className="absolute top-4 left-4 right-4 flex justify-between items-start font-mono text-[10px] tracking-[0.22em] uppercase text-[#F7F7F9]/70 pointer-events-none">
              <span>
                {String(idx + 1).padStart(2, "0")} / {String(gallery.length || 1).padStart(2, "0")}
              </span>
              <span>{current.type?.toUpperCase()}</span>
            </div>
            <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end font-mono text-[10px] tracking-[0.22em] uppercase text-[#F7F7F9]/70 pointer-events-none">
              <span>{current.label?.[lang]}</span>
              <span>LAKA — {project.year}</span>
            </div>

            {/* Nav arrows */}
            {gallery.length > 1 && (
              <>
                <button
                  data-testid="gallery-prev"
                  onClick={() => setIdx((i) => (i - 1 + gallery.length) % gallery.length)}
                  className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-[#0A0A0A]/80 hover:bg-[#D35400] flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100 duration-300"
                  aria-label="Previous"
                >
                  <ArrowLeft size={18} />
                </button>
                <button
                  data-testid="gallery-next"
                  onClick={() => setIdx((i) => (i + 1) % gallery.length)}
                  className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-[#0A0A0A]/80 hover:bg-[#D35400] flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100 duration-300"
                  aria-label="Next"
                >
                  <ArrowRight size={18} />
                </button>
              </>
            )}
          </motion.div>

          {/* Thumbnails */}
          {gallery.length > 1 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-16 md:mb-24">
              {gallery.map((g, i) => (
                <button
                  key={g.url}
                  data-testid={`gallery-thumb-${i}`}
                  onClick={() => setIdx(i)}
                  className={`relative aspect-[4/3] bg-[#FAFAF7] overflow-hidden border transition-all ${
                    idx === i ? "border-[#D35400]" : "border-[#F7F7F9]/10 hover:border-[#F7F7F9]/40"
                  }`}
                >
                  {g.kind === "video" ? (
                    <>
                      <video
                        src={g.url}
                        muted
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-cover bg-[#0A0A0A]"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-[#0A0A0A]/30">
                        <div className="w-12 h-12 rounded-full bg-[#D35400] flex items-center justify-center">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </>
                  ) : (
                    <img src={g.url} alt={g.label?.[lang]} className="w-full h-full object-contain" />
                  )}
                  <div
                    className={`absolute bottom-0 left-0 right-0 px-3 py-2 font-mono text-[9px] tracking-[0.2em] uppercase ${
                      idx === i ? "bg-[#D35400] text-white" : "bg-[#0A0A0A]/80 text-[#F7F7F9]/80"
                    }`}
                  >
                    {String(i + 1).padStart(2, "0")} · {g.label?.[lang]}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Description */}
          {project.description && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease }}
              className="grid grid-cols-12 gap-8 md:gap-12 border-t border-[#F7F7F9]/10 pt-14 md:pt-20"
            >
              <div className="col-span-12 md:col-span-4">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F7F7F9]/50">
                  {lang === "fr" ? "Note d'intention" : "Project note"}
                </div>
              </div>
              <div className="col-span-12 md:col-span-8">
                <p className="text-lg md:text-xl font-light leading-relaxed text-[#F7F7F9]/90 max-w-[60ch]">
                  {project.description[lang]}
                </p>

                <button
                  data-testid="project-detail-contact"
                  onClick={() => {
                    onClose();
                    setTimeout(() => {
                      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                    }, 400);
                  }}
                  className="mt-10 group inline-flex items-center gap-3 border border-[#F7F7F9]/40 text-[#F7F7F9] px-6 py-4 text-sm tracking-wide hover:bg-[#D35400] hover:border-[#D35400] transition-colors duration-500"
                >
                  {lang === "fr" ? "Discuter d'un projet similaire" : "Discuss a similar project"}
                  <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-500" />
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function Meta({ k, v }) {
  return (
    <div className="border-b border-[#F7F7F9]/10 pb-3">
      <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#F7F7F9]/50 mb-1">
        {k}
      </div>
      <div className="text-base">{v}</div>
    </div>
  );
}
